/**
 * WareBee Digital Twin - Labour KPIs
 * Metrics related to worker efficiency, utilization, and well-being
 */

/**
 * Labour KPIs
 */
export const labourKpis /*: WarehouseKPI[] */ = [
  {
    id: 'labor_utilization',
    category: 'labour',
    title: 'Labor Utilization',
    description:
      'Percentage of scheduled time that workers are actively working',
    unit: '%',
    formula: 'SUM(active_work_time) / SUM(scheduled_time) * 100',
    tags: ['labor', 'utilization', 'iso-22400:WU'],
    benchmark: {
      standards: {
        site: {
          target: 90,
          warning: { lower: 85 },
          critical: { lower: 80 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 80 },
            { label: 'Warning', min: 80.01, max: 85 },
            { label: 'Target', min: 85.01, max: 90 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['shift', 'department', 'employee_id'],
    alertTopic: 'warehouse.alerts.labor.utilization',
  },
  {
    id: 'labor_efficiency',
    category: 'labour',
    title: 'Labor Efficiency',
    description: 'Ratio of standard time to actual time for tasks',
    unit: '%',
    formula: 'SUM(standard_time) / SUM(actual_time) * 100',
    tags: ['labor', 'efficiency', 'iso-22400:WE'],
    benchmark: {
      standards: {
        site: {
          target: 95,
          warning: { lower: 90 },
          critical: { lower: 85 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 85 },
            { label: 'Warning', min: 85.01, max: 90 },
            { label: 'Target', min: 90.01, max: 95 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['process', 'shift', 'employee_id'],
    alertTopic: 'warehouse.alerts.labor.efficiency',
  },
  {
    id: 'overtime_percentage',
    category: 'labour',
    title: 'Overtime Percentage',
    description: 'Percentage of labor hours that are overtime',
    unit: '%',
    formula: 'SUM(overtime_hours) / SUM(total_hours) * 100',
    tags: ['labor', 'cost'],
    benchmark: {
      standards: {
        site: {
          target: 5,
          warning: { upper: 10 },
          critical: { upper: 15 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 15 },
            { label: 'Warning', min: 15.01, max: 10 },
            { label: 'Target', min: 10.01, max: 5 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['department', 'shift', 'day_of_week'],
    alertTopic: 'warehouse.alerts.labor.overtime',
  },
  {
    id: 'worker_fatigue_index',
    category: 'labour',
    title: 'Worker Fatigue Index',
    description:
      'Composite score indicating worker fatigue risk based on activity patterns',
    unit: 'score',
    formula:
      'weighted_avg(continuous_work_time, weight_carried, distance_traveled, repetitive_motions)',
    tags: ['labor', 'safety'],
    benchmark: {
      standards: {
        site: {
          target: 25,
          warning: { upper: 50 },
          critical: { upper: 75 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P7D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 75 },
            { label: 'Warning', min: 75.01, max: 50 },
            { label: 'Target', min: 50.01, max: 25 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P7D',
    drillDimensions: ['employee_id', 'shift_length', 'process_type'],
    alertTopic: 'warehouse.alerts.labor.fatigue',
  },
  {
    id: 'total_ftes',
    category: 'labour',
    title: 'Total FTEs',
    description: 'Total number of full-time equivalent employees',
    unit: 'FTE',
    formula: 'SUM(fte_count)',
    tags: ['labor', 'fte'],
    benchmark: {
      standards: {
        site: {
          target: 40,
          warning: { lower: 30 },
          critical: { lower: 20 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 20 },
            { label: 'Warning', min: 20.01, max: 30 },
            { label: 'Target', min: 30.01, max: 40 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['department', 'shift'],
    alertTopic: 'warehouse.alerts.labor.total_ftes',
  },
  {
    id: 'productivity_score',
    category: 'labour',
    title: 'Productivity Score',
    description: 'Composite productivity score for labor',
    unit: '%',
    formula: 'AVG(productivity_score)',
    tags: ['labor', 'productivity'],
    benchmark: {
      standards: {
        site: {
          target: 90,
          warning: { lower: 80 },
          critical: { lower: 70 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 70 },
            { label: 'Warning', min: 70.01, max: 80 },
            { label: 'Target', min: 80.01, max: 90 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['department', 'shift'],
    alertTopic: 'warehouse.alerts.labor.productivity_score',
  },
  {
    id: 'labor_cost_per_unit',
    category: 'labour',
    title: 'Labor Cost Per Unit',
    description: 'Average labor cost per unit processed',
    unit: '$',
    formula: 'SUM(labor_cost) / SUM(units_processed)',
    tags: ['labor', 'cost'],
    benchmark: {
      standards: {
        site: {
          target: 1,
          warning: { upper: 2 },
          critical: { upper: 3 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 3 },
            { label: 'Warning', min: 3.01, max: 2 },
            { label: 'Target', min: 2.01, max: 1 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['department', 'shift'],
    alertTopic: 'warehouse.alerts.labor.labor_cost_per_unit',
  },
  {
    id: 'turnover_rate',
    category: 'labour',
    title: 'Turnover Rate',
    description: 'Percentage of employees leaving during the period',
    unit: '%',
    formula: '(COUNT(leavers) / AVG(employee_count)) * 100',
    tags: ['labor', 'turnover'],
    benchmark: {
      standards: {
        site: {
          target: 10,
          warning: { upper: 15 },
          critical: { upper: 20 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P365D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 20 },
            { label: 'Warning', min: 20.01, max: 15 },
            { label: 'Target', min: 15.01, max: 10 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P365D',
    drillDimensions: ['department'],
    alertTopic: 'warehouse.alerts.labor.turnover_rate',
  },
  {
    id: 'training_completion',
    category: 'labour',
    title: 'Training Completion',
    description: 'Percentage of employees who completed required training',
    unit: '%',
    formula: '(COUNT(training_completed) / COUNT(employees)) * 100',
    tags: ['labor', 'training'],
    benchmark: {
      standards: {
        site: {
          target: 100,
          warning: { lower: 95 },
          critical: { lower: 90 },
          basis: 'Legacy benchmark migrated',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P90D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 90 },
            { label: 'Warning', min: 90.01, max: 95 },
            { label: 'Target', min: 95.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'baseline' },
    },
    trendWindow: 'P90D',
    drillDimensions: ['department'],
    alertTopic: 'warehouse.alerts.labor.training_completion',
  },
  // New KPIs
  {
    id: 'ftes_per_day',
    category: 'labour',
    title: 'FTEs per Day',
    description: 'Number of full-time equivalent workers per day',
    unit: 'FTE',
    formula: 'SUM(daily_fte_count)',
    tags: ['labor', 'fte', 'daily'],
    benchmark: {
      standards: {
        site: {
          target: 20,
          warning: { lower: 18, upper: 25 },
          critical: { lower: 15, upper: 30 },
          basis: 'Resource planning standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical Low', min: 0, max: 15 },
            { label: 'Warning Low', min: 15.01, max: 18 },
            { label: 'Target', min: 18.01, max: 25 },
            { label: 'Warning High', min: 25.01, max: 30 },
            { label: 'Critical High', min: 30.01, max: 100 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['department', 'shift', 'day_of_week'],
    alertTopic: 'warehouse.alerts.labor.ftes_per_day',
  },
  {
    id: 'shift_utilization',
    category: 'labour',
    title: 'Shift Utilization',
    description: 'Percentage of shift hours used for productive work',
    unit: '%',
    formula: 'SUM(productive_hours) / SUM(total_shift_hours) * 100',
    tags: ['labor', 'shift', 'utilization'],
    benchmark: {
      standards: {
        site: {
          target: 85,
          warning: { lower: 75 },
          critical: { lower: 65 },
          basis: 'Resource planning standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 65 },
            { label: 'Warning', min: 65.01, max: 75 },
            { label: 'Target', min: 75.01, max: 85 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['shift', 'department', 'day_of_week'],
    alertTopic: 'warehouse.alerts.labor.shift_utilization',
  },
  {
    id: 'shift_utilization_net',
    category: 'labour',
    title: 'Shift Utilization (Net)',
    description:
      'Percentage of shift hours used for productive work, excluding breaks',
    unit: '%',
    formula:
      'SUM(productive_hours) / (SUM(total_shift_hours) - SUM(break_hours)) * 100',
    tags: ['labor', 'shift', 'utilization', 'net'],
    benchmark: {
      standards: {
        site: {
          target: 92,
          warning: { lower: 85 },
          critical: { lower: 75 },
          basis: 'Resource planning standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 75 },
            { label: 'Warning', min: 75.01, max: 85 },
            { label: 'Target', min: 85.01, max: 92 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['shift', 'department', 'day_of_week'],
    alertTopic: 'warehouse.alerts.labor.shift_utilization_net',
  },
  {
    id: 'hourly_productivity',
    category: 'labour',
    title: 'Hourly Productivity',
    description: 'Productivity rate per hour throughout the shift',
    unit: 'units/hr',
    formula: '(SUM(processed_units) / SUM(hours_worked)) GROUP BY hour_of_day',
    tags: ['labor', 'productivity', 'hourly'],
    benchmark: {
      standards: {
        site: {
          target: 120,
          warning: { lower: 100 },
          critical: { lower: 80 },
          basis: 'Production standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical', min: 0, max: 80 },
            { label: 'Warning', min: 80.01, max: 100 },
            { label: 'Target', min: 100.01, max: 120 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['hour_of_day', 'department', 'process_type'],
    alertTopic: 'warehouse.alerts.labor.hourly_productivity',
  },
  {
    id: 'break_detection',
    category: 'labour',
    title: 'Break Detection',
    description: 'Analysis of breaks taken during shifts',
    unit: 'min',
    formula: 'AVG(break_duration) GROUP BY hour_of_day',
    tags: ['labor', 'break', 'time'],
    benchmark: {
      standards: {
        site: {
          target: 15,
          warning: { lower: 10, upper: 20 },
          critical: { lower: 5, upper: 30 },
          basis: 'Labor standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Critical Low', min: 0, max: 5 },
            { label: 'Warning Low', min: 5.01, max: 10 },
            { label: 'Target', min: 10.01, max: 20 },
            { label: 'Warning High', min: 20.01, max: 30 },
            { label: 'Critical High', min: 30.01, max: 60 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['hour_of_day', 'department', 'shift_id'],
    alertTopic: 'warehouse.alerts.labor.break_detection',
  },
  {
    id: 'user_fatigue_distance',
    category: 'labour',
    title: 'User Fatigue - Distance',
    description: 'Average distance traveled by workers as a fatigue indicator',
    unit: 'm',
    formula: 'AVG(distance_traveled) GROUP BY hour_of_day',
    tags: ['labor', 'fatigue', 'distance'],
    benchmark: {
      standards: {
        site: {
          target: 5000,
          warning: { upper: 6500 },
          critical: { upper: 8000 },
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P7D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 5000 },
            { label: 'Warning', min: 5001, max: 6500 },
            { label: 'Critical', min: 6501, max: 10000 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P7D',
    drillDimensions: ['employee_id', 'department', 'process_type'],
    alertTopic: 'warehouse.alerts.labor.user_fatigue_distance',
  },
  {
    id: 'user_fatigue_weight',
    category: 'labour',
    title: 'User Fatigue - Weight',
    description: 'Average weight handled by workers as a fatigue indicator',
    unit: 'kg',
    formula: 'AVG(weight_handled) GROUP BY hour_of_day',
    tags: ['labor', 'fatigue', 'weight'],
    benchmark: {
      standards: {
        site: {
          target: 1500,
          warning: { upper: 2000 },
          critical: { upper: 2500 },
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P7D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 1500 },
            { label: 'Warning', min: 1501, max: 2000 },
            { label: 'Critical', min: 2001, max: 3000 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P7D',
    drillDimensions: ['employee_id', 'department', 'process_type'],
    alertTopic: 'warehouse.alerts.labor.user_fatigue_weight',
  },
  {
    id: 'user_fatigue_volume',
    category: 'labour',
    title: 'User Fatigue - Volume',
    description: 'Average volume handled by workers as a fatigue indicator',
    unit: 'm³',
    formula: 'AVG(volume_handled) GROUP BY hour_of_day',
    tags: ['labor', 'fatigue', 'volume'],
    benchmark: {
      standards: {
        site: {
          target: 5,
          warning: { upper: 7 },
          critical: { upper: 10 },
          basis: 'Ergonomic standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P7D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 7 },
            { label: 'Critical', min: 7.01, max: 15 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P7D',
    drillDimensions: ['employee_id', 'department', 'process_type'],
    alertTopic: 'warehouse.alerts.labor.user_fatigue_volume',
  },
  {
    id: 'same_task_productivity_variance',
    category: 'labour',
    title: 'Same Task Productivity Variance',
    description:
      'Variance in productivity when same user performs same task on different days',
    unit: '%',
    formula:
      'STDEV(productivity) / AVG(productivity) * 100 GROUP BY employee_id, task_type, location_id',
    tags: ['labor', 'productivity', 'variance'],
    benchmark: {
      standards: {
        site: {
          target: 5,
          warning: { upper: 10 },
          critical: { upper: 15 },
          basis: 'Performance standard',
        },
      },
      primarySource: 'site',
      timeWindows: [
        {
          name: 'default',
          duration: 'P28D',
          thresholdType: 'static',
          ranges: [
            { label: 'Target', min: 0, max: 5 },
            { label: 'Warning', min: 5.01, max: 10 },
            { label: 'Critical', min: 10.01, max: 20 },
          ],
        },
      ],
      metadata: { source: 'new kpi' },
    },
    trendWindow: 'P28D',
    drillDimensions: ['employee_id', 'task_type', 'location_id'],
    alertTopic: 'warehouse.alerts.labor.task_productivity_variance',
  },
];

export default labourKpis;
